import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_WEB = `${BACKEND_URL}/api/web`;

const getHeaders = () => {
  const token = localStorage.getItem('sellmu_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const api = {
  // Auth
  login: (data) => axios.post(`${API_WEB}/auth/login`, data),
  register: (data) => axios.post(`${API_WEB}/auth/register`, data),
  me: () => axios.get(`${API_WEB}/auth/me`, { headers: getHeaders() }),

  // Dashboard
  dashboardStats: () => axios.get(`${API_WEB}/dashboard/stats`, { headers: getHeaders() }),
  salesChart: (days = 7) => axios.get(`${API_WEB}/dashboard/sales-chart?days=${days}`, { headers: getHeaders() }),
  fastMovement: () => axios.get(`${API_WEB}/dashboard/fast-movement`, { headers: getHeaders() }),

  // Upload
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_WEB}/upload`, formData, {
      headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' }
    });
  },

  // Stores
  listStores: (page = 1, limit = 20) => axios.get(`${API_WEB}/stores?page=${page}&limit=${limit}`, { headers: getHeaders() }),
  createStore: (data) => axios.post(`${API_WEB}/stores`, data, { headers: getHeaders() }),
  getStore: (id) => axios.get(`${API_WEB}/stores/${id}`, { headers: getHeaders() }),
  updateStore: (id, data) => axios.put(`${API_WEB}/stores/${id}`, data, { headers: getHeaders() }),
  deleteStore: (id) => axios.delete(`${API_WEB}/stores/${id}`, { headers: getHeaders() }),

  // Invites
  listInvites: (storeId) => axios.get(`${API_WEB}/invites${storeId ? `?store_id=${storeId}` : ''}`, { headers: getHeaders() }),
  createInvite: (data) => axios.post(`${API_WEB}/invites`, data, { headers: getHeaders() }),
  cancelInvite: (id) => axios.delete(`${API_WEB}/invites/${id}`, { headers: getHeaders() }),
  resendInvite: (id) => axios.post(`${API_WEB}/invites/resend/${id}`, {}, { headers: getHeaders() }),
  verifyInvite: (token) => axios.get(`${API_WEB}/invites/verify/${token}`),
  acceptInvite: (token, data) => axios.post(`${API_WEB}/invites/accept/${token}`, data),

  // Employees
  listStoreEmployees: (storeId) => axios.get(`${API_WEB}/stores/${storeId}/employees`, { headers: getHeaders() }),
  listAllEmployees: () => axios.get(`${API_WEB}/employees`, { headers: getHeaders() }),
  createEmployee: (data) => axios.post(`${API_WEB}/employees`, data, { headers: getHeaders() }),
  updateEmployee: (id, data) => axios.put(`${API_WEB}/employees/${id}`, data, { headers: getHeaders() }),
  deleteEmployee: (id) => axios.delete(`${API_WEB}/employees/${id}`, { headers: getHeaders() }),

  // Categories
  listCategories: (page = 1, limit = 20) => axios.get(`${API_WEB}/categories?page=${page}&limit=${limit}`, { headers: getHeaders() }),
  listAllCategories: () => axios.get(`${API_WEB}/categories/all`, { headers: getHeaders() }),
  createCategory: (data) => axios.post(`${API_WEB}/categories`, data, { headers: getHeaders() }),
  updateCategory: (id, data) => axios.put(`${API_WEB}/categories/${id}`, data, { headers: getHeaders() }),
  deleteCategory: (id) => axios.delete(`${API_WEB}/categories/${id}`, { headers: getHeaders() }),

  // Products
  listProducts: (page = 1, limit = 20, storeId, categoryId) => {
    let url = `${API_WEB}/products?page=${page}&limit=${limit}`;
    if (storeId) url += `&store_id=${storeId}`;
    if (categoryId) url += `&category_id=${categoryId}`;
    return axios.get(url, { headers: getHeaders() });
  },
  createProduct: (data) => axios.post(`${API_WEB}/products`, data, { headers: getHeaders() }),
  getProduct: (id) => axios.get(`${API_WEB}/products/${id}`, { headers: getHeaders() }),
  updateProduct: (id, data) => axios.put(`${API_WEB}/products/${id}`, data, { headers: getHeaders() }),
  deleteProduct: (id) => axios.delete(`${API_WEB}/products/${id}`, { headers: getHeaders() }),

  // Vendors
  listVendors: (page = 1, limit = 20) => axios.get(`${API_WEB}/vendors?page=${page}&limit=${limit}`, { headers: getHeaders() }),
  createVendor: (data) => axios.post(`${API_WEB}/vendors`, data, { headers: getHeaders() }),
  updateVendor: (id, data) => axios.put(`${API_WEB}/vendors/${id}`, data, { headers: getHeaders() }),
  deleteVendor: (id) => axios.delete(`${API_WEB}/vendors/${id}`, { headers: getHeaders() }),

  // Customers
  listCustomers: (page = 1, limit = 20) => axios.get(`${API_WEB}/customers?page=${page}&limit=${limit}`, { headers: getHeaders() }),
  createCustomer: (data) => axios.post(`${API_WEB}/customers`, data, { headers: getHeaders() }),
  updateCustomer: (id, data) => axios.put(`${API_WEB}/customers/${id}`, data, { headers: getHeaders() }),
  deleteCustomer: (id) => axios.delete(`${API_WEB}/customers/${id}`, { headers: getHeaders() }),

  // Vouchers
  listVouchers: (page = 1, limit = 20) => axios.get(`${API_WEB}/vouchers?page=${page}&limit=${limit}`, { headers: getHeaders() }),
  createVoucher: (data) => axios.post(`${API_WEB}/vouchers`, data, { headers: getHeaders() }),
  updateVoucher: (id, data) => axios.put(`${API_WEB}/vouchers/${id}`, data, { headers: getHeaders() }),
  deleteVoucher: (id) => axios.delete(`${API_WEB}/vouchers/${id}`, { headers: getHeaders() }),

  // Transactions
  listTransactions: (page = 1, limit = 20, storeId) => {
    let url = `${API_WEB}/transactions?page=${page}&limit=${limit}`;
    if (storeId) url += `&store_id=${storeId}`;
    return axios.get(url, { headers: getHeaders() });
  },
  createTransaction: (data) => axios.post(`${API_WEB}/transactions`, data, { headers: getHeaders() }),

  // POS Sessions
  openSession: (data) => axios.post(`${API_WEB}/pos/sessions/open`, data, { headers: getHeaders() }),
  closeSession: (id, data) => axios.post(`${API_WEB}/pos/sessions/${id}/close`, data, { headers: getHeaders() }),
  listSessions: (page = 1, limit = 20, storeId) => {
    let url = `${API_WEB}/pos/sessions?page=${page}&limit=${limit}`;
    if (storeId) url += `&store_id=${storeId}`;
    return axios.get(url, { headers: getHeaders() });
  },

  // Withdrawals
  listWithdrawals: (page = 1, limit = 20) => axios.get(`${API_WEB}/withdrawals?page=${page}&limit=${limit}`, { headers: getHeaders() }),
  createWithdrawal: (data) => axios.post(`${API_WEB}/withdrawals`, data, { headers: getHeaders() }),
  deleteWithdrawal: (id) => axios.delete(`${API_WEB}/withdrawals/${id}`, { headers: getHeaders() }),

  // Commissions
  listCommissions: (page = 1, limit = 20, staffId) => {
    let url = `${API_WEB}/commissions?page=${page}&limit=${limit}`;
    if (staffId) url += `&staff_id=${staffId}`;
    return axios.get(url, { headers: getHeaders() });
  },

  // Settings
  getSettings: () => axios.get(`${API_WEB}/settings`, { headers: getHeaders() }),
  updateSettings: (data) => axios.put(`${API_WEB}/settings`, data, { headers: getHeaders() }),

  // Stock
  stockIn: (data) => axios.post(`${API_WEB}/stock/in`, data, { headers: getHeaders() }),
  stockOpname: (items) => axios.post(`${API_WEB}/stock/opname`, items, { headers: getHeaders() }),
  stockLogs: (page = 1, limit = 20, productId) => {
    let url = `${API_WEB}/stock/logs?page=${page}&limit=${limit}`;
    if (productId) url += `&product_id=${productId}`;
    return axios.get(url, { headers: getHeaders() });
  },
};

export default api;
